import React, { useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  Grid,
  GridItem,
  HelperText,
  HelperTextItem,
  InputGroup,
  InputGroupItem,
  Label,
  Spinner,
  TextInput,
  Title,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { EyeIcon, EyeSlashIcon, SearchIcon } from '@patternfly/react-icons';
import { formatPrice } from '../../../../utils/money';
import type { DomainTransferabilityResult } from '../../../../types/models';
import {
  MAX_TRANSFER_CART,
  conflictCopy,
  isLikelyFqdn,
  normalizeDomainInput,
} from './types';
import type { TransferCartItem } from './types';

interface TransferDomainsStepProps {
  // Controlled add-row inputs (lifted to the page so they survive step navigation).
  domainInput: string;
  onDomainInputChange: (value: string) => void;
  authCode: string;
  onAuthCodeChange: (value: string) => void;
  // Transferability check.
  results: DomainTransferabilityResult[];
  checking: boolean;
  checkError: string | null;
  /** Run the transferability check for the given (raw) domain input. */
  onCheck: (domain: string) => void;
  // Cart.
  cart: TransferCartItem[];
  onAddToCart: (item: TransferCartItem) => void;
  onRemoveFromCart: (domain: string) => void;
  /** Per-name create-time errors keyed by FQDN — flag the matching cart rows after a failed submit. */
  itemErrors: Record<string, string>;
}

const TransferDomainsStep: React.FunctionComponent<TransferDomainsStepProps> = ({
  domainInput,
  onDomainInputChange,
  authCode,
  onAuthCodeChange,
  results,
  checking,
  checkError,
  onCheck,
  cart,
  onAddToCart,
  onRemoveFromCart,
  itemErrors,
}) => {
  const [showAuthCode, setShowAuthCode] = useState(false);

  const normalizedInput = normalizeDomainInput(domainInput);
  const canCheck = isLikelyFqdn(domainInput) && !checking;
  // Show the verdict only for the domain currently in the input — editing the input after a check
  // hides the (now stale) verdict until the customer checks again.
  const verdict = normalizedInput !== '' ? results.find((r) => r.domain === normalizedInput) ?? null : null;

  const inCart = verdict !== null && cart.some((c) => c.domain === verdict.domain);
  const authCodeEntered = authCode.trim() !== '';
  const canAdd =
    verdict !== null && verdict.supported && verdict.conflict === null && authCodeEntered && !inCart;
  const cartFull = cart.length >= MAX_TRANSFER_CART;

  const triggerCheck = () => {
    if (canCheck) {
      onCheck(domainInput);
    }
  };

  const handleAdd = () => {
    if (!verdict || !canAdd || cartFull) {
      return;
    }
    onAddToCart({
      domain: verdict.domain,
      tld: verdict.tld,
      price: verdict.price,
      authCode: authCode.trim(),
    });
  };

  const renderVerdict = () => {
    if (checking) {
      return (
        <Bullseye>
          <Spinner aria-label="Checking transferability" />
        </Bullseye>
      );
    }
    if (checkError) {
      return (
        <Alert variant="danger" isInline title="The transferability check failed">
          {checkError}
        </Alert>
      );
    }
    if (!verdict) {
      return null;
    }
    return (
      <Card>
        <CardBody>
          <DescriptionList isHorizontal isCompact>
            <DescriptionListGroup>
              <DescriptionListTerm>Domain</DescriptionListTerm>
              <DescriptionListDescription>{verdict.domain}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Transfer price (excl. VAT)</DescriptionListTerm>
              <DescriptionListDescription>
                {verdict.supported && verdict.price !== null ? (
                  <>
                    {formatPrice(verdict.price)}
                    {verdict.is_custom && (
                      <Label color="purple" isCompact className="pf-v6-u-ml-sm">
                        custom
                      </Label>
                    )}
                  </>
                ) : (
                  <Content component="small">Transfer not offered for this extension</Content>
                )}
              </DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>

          {verdict.conflict !== null && (
            <Alert variant="warning" isInline isPlain title={conflictCopy(verdict.conflict)} className="pf-v6-u-mt-md" />
          )}

          {/* registrar_signal is advisory only — never a gate. */}
          <HelperText className="pf-v6-u-mt-md">
            {verdict.registrar_signal === 'transferable' ? (
              <HelperTextItem variant="success">
                The current registrar confirms this domain can be transferred.
              </HelperTextItem>
            ) : (
              <HelperTextItem>
                We couldn&apos;t pre-confirm the transfer with the current registrar — you can still start it.
              </HelperTextItem>
            )}
          </HelperText>

          <div className="pf-v6-u-mt-md">
            {inCart ? (
              <Label color="blue">In cart</Label>
            ) : (
              <>
                <Button variant="secondary" onClick={handleAdd} isDisabled={!canAdd || cartFull}>
                  Add to cart
                </Button>
                {verdict.supported && verdict.conflict === null && !authCodeEntered && (
                  <Content component="small" className="pf-v6-u-ml-sm">
                    Enter the transfer code above to add this domain.
                  </Content>
                )}
                {cartFull && (
                  <Content component="small" className="pf-v6-u-ml-sm">
                    Your cart is full (max {MAX_TRANSFER_CART}).
                  </Content>
                )}
              </>
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  return (
    <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsLg' }}>
      <FlexItem>
        <Title headingLevel="h2" size="lg">
          Domains &amp; transfer codes
        </Title>
        <Content component="p">
          Enter a domain you already own elsewhere (e.g. <code>example.be</code>) together with its transfer code, then
          check whether it can be transferred to us. Add each transferable domain to your cart (up to {MAX_TRANSFER_CART}
          ).
        </Content>
      </FlexItem>

      <FlexItem>
        <Form onSubmit={(event) => event.preventDefault()}>
          <Grid hasGutter>
            <GridItem md={7}>
              <FormGroup label="Domain" fieldId="transfer-domain" isRequired>
                <TextInput
                  id="transfer-domain"
                  type="text"
                  aria-label="Domain to transfer in"
                  placeholder="example.be"
                  value={domainInput}
                  onChange={(_event, value) => onDomainInputChange(value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      triggerCheck();
                    }
                  }}
                />
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem>The full domain you already own elsewhere.</HelperTextItem>
                  </HelperText>
                </FormHelperText>
              </FormGroup>
            </GridItem>
            <GridItem md={5}>
              <FormGroup label="Transfer (EPP) code" fieldId="transfer-auth-code" isRequired>
                <InputGroup>
                  <InputGroupItem isFill>
                    <TextInput
                      id="transfer-auth-code"
                      type={showAuthCode ? 'text' : 'password'}
                      aria-label="Transfer authorization code"
                      value={authCode}
                      onChange={(_event, value) => onAuthCodeChange(value)}
                    />
                  </InputGroupItem>
                  <InputGroupItem>
                    <Button
                      variant="control"
                      aria-label={showAuthCode ? 'Hide the transfer code' : 'Show the transfer code'}
                      icon={showAuthCode ? <EyeSlashIcon /> : <EyeIcon />}
                      onClick={() => setShowAuthCode((v) => !v)}
                    />
                  </InputGroupItem>
                </InputGroup>
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem>
                      The transfer/EPP code from your current registrar — sometimes called the authorization or EPP
                      code.
                    </HelperTextItem>
                  </HelperText>
                </FormHelperText>
              </FormGroup>
            </GridItem>
          </Grid>
          <div>
            <Button variant="primary" icon={<SearchIcon />} onClick={triggerCheck} isDisabled={!canCheck} isLoading={checking}>
              Check
            </Button>
          </div>
        </Form>
      </FlexItem>

      <FlexItem>{renderVerdict()}</FlexItem>

      <FlexItem>
        <Title headingLevel="h3" size="md">
          Your cart ({cart.length})
        </Title>
        {cart.length === 0 ? (
          <HelperText>
            <HelperTextItem>Add at least one transferable domain to continue.</HelperTextItem>
          </HelperText>
        ) : (
          <Table aria-label="Domains to transfer" variant="compact" className="pf-v6-u-mt-sm">
            <Thead>
              <Tr>
                <Th>Domain</Th>
                <Th>TLD</Th>
                <Th>Transfer price (excl. VAT)</Th>
                <Th>Transfer code</Th>
                <Th screenReaderText="Remove from cart" />
              </Tr>
            </Thead>
            <Tbody>
              {cart.map((c) => {
                const rowError = itemErrors[c.domain];
                return (
                  <Tr key={c.domain}>
                    <Td dataLabel="Domain">
                      {c.domain}
                      {rowError && (
                        <FormHelperText>
                          <HelperText>
                            <HelperTextItem variant="error">{conflictCopy(rowError)}</HelperTextItem>
                          </HelperText>
                        </FormHelperText>
                      )}
                    </Td>
                    <Td dataLabel="TLD">.{c.tld}</Td>
                    <Td dataLabel="Transfer price (excl. VAT)">
                      {c.price !== null ? formatPrice(c.price) : <Content component="small">—</Content>}
                    </Td>
                    <Td dataLabel="Transfer code">
                      <span aria-label="Transfer code hidden">••••••</span>
                    </Td>
                    <Td dataLabel="Remove from cart" modifier="fitContent">
                      <Button
                        variant="link"
                        isInline
                        isDanger
                        onClick={() => onRemoveFromCart(c.domain)}
                        aria-label={`Remove ${c.domain} from cart`}
                      >
                        Remove
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </FlexItem>
    </Flex>
  );
};

export default TransferDomainsStep;
